import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
  updateDoc,
} from 'firebase/firestore';
import { db } from './config';
import { initUserProfile } from './auth';
import { advanceAfterPlay, previousDay } from '../utils/calendarStreak';
import { updateLeaderboardEntry } from './leaderboardService';

function isOfflineError(err) {
  return err?.code === 'unavailable' || err?.message?.includes('offline');
}

const DAILY_COLLECTION = 'dailyWordMaster';

function playRef(dateId, uid) {
  return doc(db, DAILY_COLLECTION, dateId, 'plays', uid);
}

export async function getUserDailyWordMasterResult(uid) {
  try {
    const dateId = new Date();
    const dateKey = `${dateId.getFullYear()}-${String(dateId.getMonth() + 1).padStart(2, '0')}-${String(dateId.getDate()).padStart(2, '0')}`;
    const snap = await getDoc(playRef(dateKey, uid));
    if (!snap.exists()) return null;
    const d = snap.data();
    return { won: !!d.win, attempts: d.guessCount | 0 };
  } catch (err) {
    if (isOfflineError(err)) return null;
    throw err;
  }
}

export async function getDailyWordMasterLeaderboard(limitVal = 10) {
  try {
    const d = new Date();
    const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const playsRef = collection(db, DAILY_COLLECTION, dateKey, 'plays');
    const q = query(playsRef, where('win', '==', true), orderBy('guessCount'), limit(limitVal));
    const snap = await getDocs(q);
    return snap.docs.map((ds) => ({
      uid: ds.id,
      displayName: (ds.data().displayName || 'Player').trim() || 'Player',
      attempts: ds.data().guessCount | 0,
    }));
  } catch (err) {
    if (isOfflineError(err)) return [];
    throw err;
  }
}

/**
 * Flutter parity: one create per day per user; updates `users/{uid}`:
 * - `wordMasterStats` (currentWinStreak/maxWinStreak/lastDailyWinDate/lastDailyCompletedDate)
 * - `globalStreak` + `lastPlayedGlobalDate`
 * and leaderboards:
 * - `wordmaster_streak` (maxWinStreak)
 * - `global_streak`
 */
export async function recordWordMasterDailyCompletion({ uid, dateId, guessCount, win, displayName }) {
  const ref = playRef(dateId, uid);
  const wrote = await runTransaction(db, async (tx) => {
    const existing = await tx.get(ref);
    if (existing.exists()) return false;
    tx.set(ref, {
      guessCount,
      win,
      displayName: displayName || 'Player',
      recordedAt: serverTimestamp(),
    });
    return true;
  });
  if (!wrote) return { wrote: false };

  const userRef = doc(db, 'users', uid);
  let userSnap = await getDoc(userRef);
  if (!userSnap.exists()) {
    await initUserProfile(uid, (displayName || 'Player').trim() || 'Player');
    userSnap = await getDoc(userRef);
  }
  if (!userSnap.exists()) return { wrote: true };
  const data = userSnap.data();

  const wm = { ...(data.wordMasterStats || {}) };
  const prevMax = (wm.maxWinStreak ?? 0) | 0;
  const currentWin = (wm.currentWinStreak ?? 0) | 0;
  const lastWin = wm.lastDailyWinDate ?? null;

  if (win) {
    let nextStreak;
    if (lastWin == null) nextStreak = 1;
    else if (lastWin === dateId) nextStreak = currentWin;
    else if (lastWin === previousDay(dateId)) nextStreak = currentWin + 1;
    else nextStreak = 1;
    wm.currentWinStreak = nextStreak;
    wm.maxWinStreak = prevMax < nextStreak ? nextStreak : prevMax;
    wm.lastDailyWinDate = dateId;
  } else {
    wm.currentWinStreak = 0;
  }
  wm.lastDailyCompletedDate = dateId;

  const currentGlobal = (data.globalStreak ?? 0) | 0;
  const lastPlayed = data.lastPlayedGlobalDate ?? null;
  const global = advanceAfterPlay({ playDate: dateId, lastPlayed, currentStreak: currentGlobal });
  const didAdvanceGlobal = global.streak > currentGlobal || global.lastPlayed !== lastPlayed;

  const name = displayName || data.displayName || 'Player';

  await updateDoc(userRef, {
    wordMasterStats: wm,
    ...(didAdvanceGlobal
      ? {
          globalStreak: global.streak,
          lastPlayedGlobalDate: global.lastPlayed,
        }
      : {}),
    updatedAt: serverTimestamp(),
  });

  // Sync streak leaderboard whenever we have a non-zero max — not only when max
  // increased this play (profile may already hold the streak without a leaderboard row).
  const wmMax = (wm.maxWinStreak | 0);
  if (wmMax > 0) {
    await updateLeaderboardEntry('wordmaster_streak', uid, name, wmMax);
  }
  if (didAdvanceGlobal) {
    await updateLeaderboardEntry('global_streak', uid, name, global.streak);
  }

  return { wrote: true };
}
